'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SeatData } from '@/components/grid';
import { getHighResAvatarUrl } from '@/lib/utils';
import { resolveBalanceAndRunway, DEFAULT_TAX_RATE_BPS } from '@/lib/tax';

interface UseSeatsOptions {
  poolId: string;
  enabled?: boolean;
}

interface ApiSeat {
  seatIndex: number;
  holder: string | null;
  holderIdentity?: {
    displayName?: string;
    avatarUrl?: string;
    level?: number;
  };
  selfAssessedPrice: string;
  balance: string;
  lastUpdateTime?: number;
  lastUpdated: string;
  metrics?: {
    currentBalance?: string;
    timeUntilForfeiture?: number;
  };
}

export function useSeats({ poolId, enabled = true }: UseSeatsOptions) {
  return useQuery({
    queryKey: ['seats', poolId],
    queryFn: async (): Promise<SeatData[]> => {
      const response = await fetch(`/api/pools/${poolId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch seats');
      }
      const data = await response.json();
      
      // Transform API response to SeatData format
      return (data.seats || []).map((seat: ApiSeat): SeatData => {
        const rawBalance = BigInt(seat.balance || '0');
        const price = BigInt(seat.selfAssessedPrice || '0');
        const lastUpdate = Number(seat.lastUpdateTime || 0);

        const { balance, runwaySeconds: runway } = seat.holder
          ? resolveBalanceAndRunway(rawBalance, price, DEFAULT_TAX_RATE_BPS, lastUpdate, seat.metrics)
          : { balance: rawBalance, runwaySeconds: 0 };

        const isForfeited = !!seat.holder && balance <= 0n;

        return {
          seatIndex: seat.seatIndex,
          holder: seat.holder,
          holderAvatar: getHighResAvatarUrl(seat.holderIdentity?.avatarUrl),
          holderName: seat.holderIdentity?.displayName,
          holderLevel: seat.holderIdentity?.level,
          selfAssessedPrice: price,
          balance,
          runwaySeconds: runway,
          lastUpdateTime: lastUpdate,
          isForfeited,
        };
      });
    },
    enabled,
    // SCALABILITY: Rely on SSE (useLiveGrid) for real-time updates
    // This query is for initial load only; live updates come via SSE
    staleTime: 60 * 1000, // 60 seconds - SSE handles real-time
    // No refetchInterval - SSE provides real-time updates
  });
}

/**
 * Get a single seat.
 *
 * Tries the React Query grid cache first (populated by useSeats / useLiveGrid)
 * to avoid an individual API call.  Falls back to `/api/seats/...` only when
 * the grid data isn't in the client-side cache.
 */
export function useSeat(poolId: string, seatIndex: number) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['seat', poolId, seatIndex],
    queryFn: async (): Promise<SeatData | null> => {
      // Fast path: derive from the already-cached grid data.
      const gridSeats = queryClient.getQueryData<SeatData[]>(['seats', poolId]);
      if (gridSeats) {
        const found = gridSeats.find((s) => s.seatIndex === seatIndex);
        if (found) return found;
      }

      // Slow path: individual fetch.
      const response = await fetch(`/api/seats/${poolId}/${seatIndex}`);
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to fetch seat');
      }
      const seat = await response.json();
      
      const rawBalance = BigInt(seat.balance || '0');
      const price = BigInt(seat.selfAssessedPrice || '0');
      const lastUpdate = Number(seat.lastUpdateTime || 0);

      const { balance, runwaySeconds: runway } = seat.holder
        ? resolveBalanceAndRunway(rawBalance, price, DEFAULT_TAX_RATE_BPS, lastUpdate, seat.metrics)
        : { balance: rawBalance, runwaySeconds: 0 };
      const isForfeited = !!seat.holder && balance <= 0n;

      return {
        seatIndex: seat.seatIndex,
        holder: seat.holder,
        holderAvatar: getHighResAvatarUrl(seat.holderIdentity?.avatarUrl),
        holderName: seat.holderIdentity?.displayName,
        holderLevel: seat.holderIdentity?.level,
        selfAssessedPrice: price,
        balance,
        runwaySeconds: runway,
        lastUpdateTime: lastUpdate,
        isForfeited,
      };
    },
    staleTime: 10 * 1000,
  });
}
